import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Grid,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  DialogActions,
  DialogContentText,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  LocationOnOutlined as LocationIcon,
  Home as HomeIcon,
  People as PeopleIcon,
  Add as AddIcon,
  ViewQuiltOutlined as FloorPlanIcon,
  Info as InfoIcon,
  ElectricBolt as FlashIcon,
  Wifi as WifiIcon,
  Close as CloseIcon,
  DeleteOutline,
  EditOutlined,
  Image,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomTextField from "../../components/common/Textfield";
import apiService from "../../service/Api";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/Index";
import Pagination from "../../components/common/Pagination";
import { logout } from "../../store/slice/AuthSlice";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";

interface VenueDocument {
  id: number;
  venue: number;
  file: string;
  file_type: string;
}

interface VenueLayouts {
  capacity: number;
  id: number;
}
interface Venue {
  id: number;
  name: string;
  address_one: string | null;
  venue_type: string | null;
  completeness_score: number | null;
  capacity: string | null;
  layouts: VenueLayouts[];
  power_type: string | null;
  internet_service_provider: string | null;
  files: VenueDocument[];
}

interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Venue[];
}

interface VenueCardProps {
  venue: Venue;
  onDelete: (venueId: string) => void;
}

// Custom Info Chip Component
const CompletenessScore = ({ score = 50, size = 70 }) => {
  const getColor = (score: number) => {
    if (score >= 80) return "#4caf50"; // Green
    if (score >= 60) return "#ff9800"; // Orange
    if (score >= 40) return "#f44336"; // Red
    return "#9e9e9e"; // Gray
  };

  const getLabel = (score: number) => {
    if (score >= 80) return "Great";
    if (score >= 60) return "Good";
    if (score >= 40) return "Fair";
    return "Poor";
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        background: "rgba(255, 255, 255, 0.95)",
        borderRadius: "50%",
        padding: "4px",
        backdropFilter: "blur(10px)",
      }}
    >
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={3}
        sx={{
          color: "#e0e0e0",
          position: "absolute",
        }}
      />
      <CircularProgress
        variant="determinate"
        value={score}
        size={size}
        thickness={3}
        sx={{
          color: getColor(score),
          "& .MuiCircularProgress-circle": {
            strokeLinecap: "round",
          },
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="body2"
          component="div"
          sx={{
            fontWeight: "bold",
            color: getColor(score),
            fontSize: "0.85rem",
          }}
        >
          {score}%
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontSize: "0.5rem", color: "text.secondary", lineHeight: 0.8 }}
        >
          {getLabel(score)}
        </Typography>
      </Box>
    </Box>
  );
};

// Define the props interface
interface InfoChipProps {
  icon: React.ReactElement; // Changed to ReactElement for React.cloneElement compatibility
  label: string | number; // Label can be a string or number
  color?: string; // Optional, defaults to "#666"
}

// Custom Info Chip Component
const InfoChip: React.FC<InfoChipProps> = ({ icon, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
    {React.cloneElement(icon, {})}
    <Typography variant="body2" sx={{ color: "#614438", fontSize: "0.8rem" }}>
      {label}
    </Typography>
  </Box>
);

const VenueCard: React.FC<VenueCardProps> = ({ venue, onDelete }) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const userPermissions = useSelector(
    (state: RootState) => state.auth.user?.permissions || []
  );
  const navigate = useNavigate();
  const [openMap, setOpenMap] = useState(false);
  const [openFloorPlan, setOpenFloorPlan] = useState(false);
  const [location, setLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [isLoadingMap, setIsLoadingMap] = useState(false);

  const hasPermission = (permission: string) => {
    return userPermissions.includes(permission.trim().replace(/'$/, ""));
  };

  const hasVenue = !!venue?.address_one;
  const primaryTextColor = reduxTheme.primary_colors?.[400] || "#9c6644";
  const secondaryTextColor = reduxTheme.neutral_colors?.[400] || "#D6DDEB";
  const buttonBackgroundColor = reduxTheme.primary_colors?.[400] || "#9c6644";
  const iconButtonBackgroundColor =
    reduxTheme.primary_colors?.[400] || "#4a3228";
  const iconColor = reduxTheme.primary_colors?.[600] || "#4a3228";

  // Filter for image files
  const imageFiles = venue.files?.filter(
    (file) => file.file_type === "venue_photos"
  );
  const primaryFile = imageFiles?.length > 0 ? imageFiles[0].file : null;

  const floorPlanFiles = venue.files?.filter(
    (img) => img.file_type === "floor_plan"
  );

  const hasFloorPlan = floorPlanFiles && floorPlanFiles.length > 0;

  const mapContainerStyle = {
    width: "100%",
    height: "400px",
  };

  const fallbackCenter = {
    lat: -17.8252,
    lng: 31.0335,
  };

  useEffect(() => {
    const fetchCoordinates = async () => {
      if (!venue?.address_one) {
        setLocation(fallbackCenter);
        return;
      }

      setIsLoadingMap(true);
      try {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            venue.address_one
          )}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
        );
        const data = await response.json();
        if (data.results?.[0]) {
          const { lat, lng } = data.results[0].geometry.location;
          setLocation({ lat, lng });
        } else {
          setLocation(fallbackCenter);
        }
      } catch (error) {
        console.error("Geocoding error:", error);
        setLocation(fallbackCenter);
      } finally {
        setIsLoadingMap(false);
      }
    };

    if (openMap) {
      fetchCoordinates();
    } else {
      setIsLoadingMap(false);
      setLocation(null);
    }
  }, [venue?.address_one, openMap]);

  const floorPlanFile = venue.files?.find(
    (doc) => doc.file_type === "floor_plan"
  );

  const isImageFile = (fileUrl: string): boolean => {
    return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileUrl);
  };

  const handleViewFloorPlan = () => {
    if (!floorPlanFile) return;

    if (isImageFile(floorPlanFile.file)) {
      setOpenFloorPlan(true);
    } else {
      window.open(floorPlanFile.file, "_blank");
    }
  };

  return (
    <>
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            "&:hover .card-actions": {
              opacity: 1,
              pointerEvents: "auto",
            },
            "&:hover .card-image": {
              transform: "scale(1.05)",
            },
          }}
        >
          {primaryFile ? (
            <CardMedia
              className="card-image"
              component="img"
              height="180"
              image={primaryFile}
              loading="lazy"
              alt={venue.name}
              sx={{
                objectFit: "cover",
                transition: "transform 0.4s ease",
              }}
            />
          ) : (
            <Box
              className="card-image"
              height={180}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                background: "linear-gradient(135deg, #E5BFA5 0%, #614438 100%)",
                color: "white",
                transition: "transform 0.4s ease",
              }}
            >
              <Box textAlign="center">
                <Image sx={{ fontSize: 50, opacity: 0.7 }} />
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                  No Image
                </Typography>
              </Box>
            </Box>
          )}

          {/* Gradient overlay for better text readability */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "40%",
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 100%)",
              pointerEvents: "none",
            }}
          />

          {/* Completeness Score Badge */}
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              zIndex: 3,
            }}
          >
            <CompletenessScore score={venue?.completeness_score ?? 50} />
          </Box>

          {/* Edit/Delete overlay */}
          {(hasPermission("venue.change_venue") ||
            hasPermission("venue.delete_venue")) && (
            <Box
              className="card-actions"
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                display: "flex",
                gap: 1,
                opacity: 0,
                pointerEvents: "none",
                transition: "all 0.3s ease",
                zIndex: 3,
              }}
            >
              {hasPermission("venue.change_venue") && (
                <Tooltip title="Edit Venue" arrow>
                  <IconButton
                    aria-label="edit"
                    size="small"
                    onClick={() => navigate(`/venues/view/${venue.id}/edit`)}
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      color: primaryTextColor,
                      backdropFilter: "blur(10px)",
                      "&:hover": {
                        backgroundColor: primaryTextColor,
                        color: "white",
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    <EditOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {hasPermission("venue.delete_venue") && (
                <Tooltip title="Delete Venue" arrow>
                  <IconButton
                    aria-label="delete"
                    size="small"
                    onClick={() => onDelete(venue.id.toString())}
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      color: "#f44336",
                      backdropFilter: "blur(10px)",
                      "&:hover": {
                        backgroundColor: "#f44336",
                        color: "white",
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
          {/* Venue Title */}
          <Typography
            variant="h6"
            component="h3"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: primaryTextColor,
              mb: 2,
              lineHeight: 1.2,
            }}
          >
            {venue.name}
          </Typography>

          {/* Address */}
          <Box sx={{ mb: 2 }}>
            <InfoChip
              icon={<LocationIcon />}
              label={venue.address_one || "Address not specified"}
              color={iconColor}
            />
          </Box>

          {/* Venue Details in Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
              mb: 2.5,
            }}
          >
            <InfoChip
              icon={<HomeIcon />}
              label={venue.venue_type || "Type not specified"}
              color={iconColor}
            />
            <InfoChip
              icon={<PeopleIcon />}
              label={
                venue?.layouts?.[0]?.capacity
                  ? `${venue?.layouts?.[0]?.capacity} people`
                  : "Capacity not specified"
              }
              color={iconColor}
            />
            <InfoChip
              icon={<FlashIcon />}
              label={venue.power_type || "No power"}
              color={iconColor}
            />
            <InfoChip
              icon={<WifiIcon />}
              label={venue.internet_service_provider || "No internet"}
              color={iconColor}
            />
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Floor Plan Button */}
            {hasPermission("venue.view_venue") && floorPlanFile && (
              <Button
                variant="contained"
                size="small"
                startIcon={<FloorPlanIcon />}
                sx={{
                  backgroundColor: buttonBackgroundColor,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  px: 1.5,
                  py: 0.5,
                  minWidth: "auto",
                  "&:hover": {
                    backgroundColor:
                      reduxTheme.primary_colors?.[500] || "#8b5a3c",
                    transform: "translateY(-1px)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
                disabled={!hasFloorPlan}
                onClick={handleViewFloorPlan}
              >
                Plan
              </Button>
            )}

            <Box sx={{ display: "flex", gap: 1, marginLeft: "auto" }}>
              {/* Location Button */}
              {hasPermission("venue.view_venue") && (
                <Tooltip
                  title={hasVenue ? "View Location" : "No address available"}
                  arrow
                >
                  <span>
                    <IconButton
                      sx={{
                        backgroundColor: hasVenue
                          ? iconButtonBackgroundColor
                          : "#e0e0e0",
                        color: hasVenue ? "white" : "#9e9e9e",
                        borderRadius: 1.5,
                        width: 36,
                        height: 36,
                        "&:hover": {
                          backgroundColor: hasVenue
                            ? reduxTheme.primary_colors?.[500] || "#8b5a3c"
                            : "#e0e0e0",
                          transform: hasVenue ? "translateY(-1px)" : "none",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#e0e0e0",
                          color: "#9e9e9e",
                        },
                      }}
                      onClick={() => {
                        if (hasVenue) {
                          setOpenMap(true);
                        }
                      }}
                      disabled={!hasVenue}
                    >
                      <LocationIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* Details Button */}
              {hasPermission("venue.view_venue") && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<InfoIcon />}
                  sx={{
                    backgroundColor: buttonBackgroundColor,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    px: 2,
                    py: 0.5,
                    "&:hover": {
                      backgroundColor:
                        reduxTheme.primary_colors?.[500] || "#8b5a3c",
                      transform: "translateY(-1px)",
                    },
                  }}
                  onClick={() => navigate(`/venues/view/${venue.id}`)}
                >
                  Details
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Map Dialog */}
      <Dialog
        open={openMap}
        onClose={() => setOpenMap(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        {isLoadingMap && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <DialogTitle
          sx={{
            color: primaryTextColor,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Location - {venue.name}
          <IconButton onClick={() => setOpenMap(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <LoadScript
            googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY!}
          >
            {location && (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                zoom={15}
                center={location}
              >
                <Marker position={location} />
              </GoogleMap>
            )}
          </LoadScript>
        </DialogContent>
      </Dialog>

      {/* Floor Plan Dialog */}
      <Dialog
        open={openFloorPlan}
        onClose={() => setOpenFloorPlan(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle
          sx={{
            color: primaryTextColor,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Floor Plan - {venue.name}
          <IconButton onClick={() => setOpenFloorPlan(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          {floorPlanFiles && floorPlanFiles.length > 0 ? (
            floorPlanFiles.map((img) => (
              <img
                key={img.id}
                src={img.file}
                alt={`Floor Plan ${img.id}`}
                style={{ maxWidth: "100%", maxHeight: 400, marginBottom: 8 }}
              />
            ))
          ) : (
            <Typography variant="body1" color="text.secondary">
              No floor plan available.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const ListVenues: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [venueType, setVenueType] = useState("");
  const [address, setAddress] = useState("");
  const [capacity, setCapacity] = useState("");
  const [venues, setVenues] = useState<Venue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const userPermissions = useSelector(
    (state: RootState) => state.auth.user?.permissions || []
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [venueToDelete, setVenueToDelete] = useState<string | null>(null);

  const hasPermission = (permission: string) => {
    return userPermissions.includes(permission.trim().replace(/'$/, ""));
  };

  const fetchVenues = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage + 1,
        page_size: pageSize,
        search: appliedSearchTerm,
        venue_type: venueType || undefined,
        capacity: capacity || undefined,
      };

      const response = await apiService.get<ApiResponse>(
        "/api/venues/venue/",
        params
      );

      if (!response.data.results || !Array.isArray(response.data.results)) {
        console.error(
          "API response does not contain results array:",
          response.data
        );
        toast.error("Unexpected response format from server.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          style: {
            backgroundColor: "#fff",
            color: "#333",
            border: "1px solid #C29363",
            zIndex: 9999,
          },
        });
        setVenues([]);
        setTotalPages(1);
      } else {
        if (response.data.results.length > 0) {
        }

        setVenues(response.data.results);
        setTotalItems(response.data.count);
        setTotalPages(Math.max(Math.ceil(response.data.count / pageSize), 1));
      }
    } catch (err: any) {
      let errorMessage = "Failed to load venues. Please try again.";
      if (err.response?.status === 401) {
        errorMessage = "Session expired. Please log in again.";
        dispatch(logout());
        navigate("/login");
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message?.includes("Network Error")) {
        errorMessage = "Network error. Please check your connection.";
      }
      console.error("Error fetching venues:", err);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        style: {
          backgroundColor: "#fff",
          color: "#333",
          border: "1px solid #C29363",
          zIndex: 9999,
        },
      });
      setVenues([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle search key down (Enter key)
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      setAppliedSearchTerm(searchTerm);
      setCurrentPage(0); // Reset to first page
    }
  };

  // Debounced search effect
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    if (searchTerm !== appliedSearchTerm) {
      debounceTimer.current = setTimeout(() => {
        setAppliedSearchTerm(searchTerm);
        setCurrentPage(0); // Reset to first page
      }, 1000);
    }
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchTerm, appliedSearchTerm]);

  // Fetch venues when pagination or search changes
  useEffect(() => {
    fetchVenues();
  }, [currentPage, appliedSearchTerm, venueType, capacity, pageSize]);

  // Handle filter changes (venue type, address, capacity)
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setCurrentPage(0); // Reset to first page (0-based)
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [venueType, address, capacity, pageSize]);

  const confirmDelete = (id: string) => {
    setVenueToDelete(id);
    setOpenDeleteDialog(true);
  };

  const handleDeleteVenue = async () => {
    if (venueToDelete === null) return;

    try {
      await apiService.delete(`/api/venues/venue/${venueToDelete}/`);
      toast.success("Venue deleted successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setOpenDeleteDialog(false);
      setVenueToDelete(null);
      fetchVenues();
    } catch (err: any) {
      let errorMessage = "Failed to delete venue.";
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Delete venue error:", err);
      setOpenDeleteDialog(false);
      setVenueToDelete(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setVenueToDelete(null);
  };

  return (
    <Box>
      <Box display="flex" gap={2} mb={4} alignItems="center" flexWrap="wrap">
        <CustomTextField
          placeholder="Search by venue name, type, or address..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchInputChange}
          onKeyDown={handleSearchKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 200,
            flexGrow: 1,
            maxWidth: 300,
            backgroundColor: reduxTheme.primary_colors[900],
          }}
        />

        {hasPermission("venue.add_venue") && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/Venues/add")}
            sx={{
              backgroundColor: reduxTheme.primary_colors[400],
              ml: "auto",
            }}
          >
            Add Venue
          </Button>
        )}
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: "#614438" }} />
        </Box>
      ) : venues.length === 0 ? (
        <Typography variant="h6" color="text.secondary" textAlign="center">
          No venues found.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {venues.map((venue) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={venue.id}>
              <VenueCard venue={venue} onDelete={confirmDelete} />
            </Grid>
          ))}
        </Grid>
      )}
      {!isLoading && venues.length > 0 && (
        <Pagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalItems}
          onPageChange={(page) => setCurrentPage(page)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(0);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this venue? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{ color: reduxTheme.neutral_colors?.[100] }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteVenue}
            sx={{ color: "#EB4335" }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListVenues;
